/** Commands whose display summary includes the first two whitespace-separated tokens. */
const TWO_WORD_COMMANDS = new Set(['git']);

/**
 * Summarizes a bash command string for compact UI display.
 * Skips comment lines that start with `#`, splits chained commands, takes one or two
 * tokens per remaining segment, and rejoins them for compact display.
 * @param command Raw bash command string.
 * @returns Display summary, or undefined when input is not a non-empty string.
 */
export function summarizeBashCommand(command: unknown): string | undefined {
  if (typeof command !== 'string') {
    return undefined;
  }

  const withoutComments = command
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith('#'))
    .join('\n');
  if (withoutComments.length === 0) {
    return undefined;
  }

  const segments = withoutComments
    .split('&&')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0 && !segment.startsWith('#'));

  if (segments.length === 0) {
    return undefined;
  }

  const summaries = segments.map(summarizeBashCommandSegment);
  return summaries.join(' && ');
}

function summarizeBashCommandSegment(segment: string): string {
  const tokens = segment
    .trim()
    .split(/\s+/)
    .filter((token) => token.length > 0);
  if (tokens.length === 0) {
    return segment;
  }

  const firstToken = tokens[0];
  if (firstToken !== undefined && TWO_WORD_COMMANDS.has(firstToken) && tokens.length >= 2) {
    return `${firstToken} ${tokens[1]}`;
  }

  return firstToken ?? segment;
}
